import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/** Session flag set when the shopper leaves for checkout from the drawer. */
const CHECKOUT_FLAG = 'cart-drawer:went-to-checkout';

/** Matches the drawer's checkout button(s), including common app-injected ones. */
const CHECKOUT_SELECTOR = [
  '[name="checkout"]',
  '#checkout',
  '.cart__checkout-button',
  '.checkout-button',
  '[class*="checkout" i]',
  '[id*="checkout" i]',
  'a[href*="/checkout"]',
  'button[type="submit"][form="cart-form"]',
].join(', ');

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, the installments CTA close-on-click, and closing on checkout.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // Checkout close: capture phase on window, so it still fires even if a
    // checkout/COD app stops the click from bubbling. Survives cart re-renders.
    window.addEventListener('click', this.#handleCheckoutClick, true);
    document.addEventListener('submit', this.#handleCheckoutSubmit, true);
    window.addEventListener('pageshow', this.#handlePageShow);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      if (this.#cameBackFromCheckout()) {
        this.#closeAfterCheckout();
      } else {
        this.#handleDrawerOpen();
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    window.removeEventListener('click', this.#handleCheckoutClick, true);
    document.removeEventListener('submit', this.#handleCheckoutSubmit, true);
    window.removeEventListener('pageshow', this.#handlePageShow);
  }

  /**
   * Hide the drawer the moment Checkout is tapped.
   * We hide it with a class rather than calling close() here, because on mobile
   * close() can touch browser history and interrupt the navigation to checkout.
   * @param {MouseEvent} event
   */
  #handleCheckoutClick = (event) => {
    const drawer = this.#themeDrawer;
    if (!drawer) return;

    // Works for clicks inside shadow roots too (e.g. app-injected buttons).
    const path = event.composedPath ? event.composedPath() : [event.target];
    if (!path.includes(drawer)) return;

    const control = path.find(
      (node) =>
        node instanceof Element &&
        node.matches('button, a, input[type="submit"], [role="button"]')
    );
    if (!(control instanceof Element) || !control.matches(CHECKOUT_SELECTOR)) return;

    this.#hideForCheckout();
  };

  /**
   * Backup: the cart form submitted with the checkout button.
   * @param {SubmitEvent} event
   */
  #handleCheckoutSubmit = (event) => {
    const submitter = /** @type {Element | null} */ (event.submitter ?? null);
    const isCheckout =
      submitter?.matches?.(CHECKOUT_SELECTOR) ||
      (event.target instanceof HTMLFormElement && /\/checkout/.test(event.target.getAttribute('action') || ''));
    if (isCheckout) this.#hideForCheckout();
  };

  /** True between the checkout tap and the drawer being reset. */
  #checkingOut = false;

  /** @type {number[]} */
  #timers = [];

  #later(fn, ms) {
    this.#timers.push(window.setTimeout(fn, ms));
  }

  #clearTimers() {
    this.#timers.forEach((id) => clearTimeout(id));
    this.#timers = [];
  }

  /**
   * Smoothly slide the drawer out when Checkout is tapped.
   * Uses CSS classes + inline fallbacks instead of close(), because on mobile
   * close() can touch browser history and interrupt the navigation to checkout.
   */
  #hideForCheckout() {
    const drawer = this.#themeDrawer;
    if (!drawer || this.#checkingOut) return; // click + submit both fire — run once
    this.#checkingOut = true;

    try {
      sessionStorage.setItem(CHECKOUT_FLAG, '1');
    } catch (_) {
      /* storage blocked — the animation below still works */
    }

    const dialog = this.#dialog;
    const rtl = document.documentElement.dir === 'rtl';

    // 1) Slide + fade the panel out (inline as a fallback if CSS is cached).
    if (dialog) {
      dialog.style.setProperty(
        'transition',
        'transform 0.32s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.24s ease 0.08s',
        'important'
      );
      // Force the start state to be painted so the transition actually runs.
      void dialog.offsetWidth;
      dialog.style.setProperty('transform', `translateX(${rtl ? '-' : ''}100%)`, 'important');
      dialog.style.setProperty('opacity', '0', 'important');
      dialog.style.setProperty('pointer-events', 'none', 'important');
    }
    drawer.classList.add('is-checking-out'); // fades the backdrop (CSS)

    // Let the page scroll again right away.
    document.documentElement.classList.remove('overflow-hidden');
    document.body.classList.remove('overflow-hidden');

    // 2) After the slide, hide it completely so nothing lingers.
    this.#later(() => {
      drawer.classList.add('is-checking-out--done');
      dialog?.style.setProperty('visibility', 'hidden', 'important');
    }, 340);

    // 3) If checkout opened a popup (e.g. a COD form app) instead of leaving the
    //    page, an open-but-hidden drawer would block it — so close it for real.
    let leaving = false;
    const markLeaving = () => (leaving = true);
    window.addEventListener('pagehide', markLeaving, { once: true });
    window.addEventListener('beforeunload', markLeaving, { once: true });
    this.#later(() => {
      if (leaving || document.visibilityState !== 'visible') return;
      this.#closeAfterCheckout();
    }, 2500);
  }

  /** Hide instantly with no animation (used when coming back to the page). */
  #hideInstantly() {
    const drawer = this.#themeDrawer;
    const dialog = this.#dialog;
    drawer?.classList.add('is-checking-out', 'is-checking-out--done');
    if (dialog) {
      dialog.style.setProperty('transition', 'none', 'important');
      dialog.style.setProperty('visibility', 'hidden', 'important');
      dialog.style.setProperty('opacity', '0', 'important');
    }
  }

  /** Clear every checkout style so the next open uses the theme's normal animation. */
  #unhide() {
    this.#clearTimers();
    this.#checkingOut = false;
    this.#themeDrawer?.classList.remove('is-checking-out', 'is-checking-out--done');
    const dialog = this.#dialog;
    if (dialog) {
      ['transition', 'transform', 'opacity', 'visibility', 'pointer-events'].forEach((prop) =>
        dialog.style.removeProperty(prop)
      );
    }
  }

  /**
   * Runs on normal loads and on back/forward cache restores (mobile Safari/Chrome).
   * If the shopper is returning from checkout, the drawer stays hidden and is
   * closed quietly — no flash of the open drawer.
   */
  #handlePageShow = () => {
    if (!this.#cameBackFromCheckout()) {
      if (!this.#checkingOut) this.#unhide();
      return;
    }
    this.#hideInstantly();
    // Let theme-drawer finish any restore it does first, then close.
    requestAnimationFrame(() => this.#closeAfterCheckout());
  };

  #cameBackFromCheckout() {
    try {
      return sessionStorage.getItem(CHECKOUT_FLAG) === '1';
    } catch (_) {
      return Boolean(this.#themeDrawer?.classList.contains('is-checking-out'));
    }
  }

  #closeAfterCheckout() {
    try {
      sessionStorage.removeItem(CHECKOUT_FLAG);
    } catch (_) {
      /* ignore */
    }

    const drawer = this.#themeDrawer;
    const dialog = this.#dialog;
    if (!drawer) return;

    // Make sure it stays invisible while the theme closes it.
    this.#hideInstantly();

    const finish = () => requestAnimationFrame(() => this.#unhide());

    if (drawer.isOpen || drawer.hasAttribute('open') || dialog?.open) {
      // Reveal styles only once the dialog has really closed (or after a safety timeout).
      let done = false;
      const onClose = () => {
        if (done) return;
        done = true;
        finish();
      };
      dialog?.addEventListener('close', onClose, { once: true });
      window.setTimeout(onClose, 800);
      drawer.close();
    } else {
      finish();
    }
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    // A restore-on-back can fire "open" — keep it hidden; pageshow will close it.
    if (this.#cameBackFromCheckout()) return;
    this.#unhide();
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}